<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("PLACEHOLDERS.vendorRequestData") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #F7941E">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <!-- Start:: Basic Information (Always Show) -->
        <div class="row">
          <div class="col-12 mb-3">
            <h5 class="section-title">{{ $t("PLACEHOLDERS.basic_info") }}</h5>
          </div>
          <!-- Start:: Avatar (if exists) -->
          <base-image-upload-input
            col="12"
            identifier="avatar"
            :placeholder="
              isStoreProvider
                ? $t('PLACEHOLDERS.store_logo')
                : $t('PLACEHOLDERS.provider_avatar')
            "
            :preSelectedImage="data.avatar"
            disabled
          />
          <!-- End:: Avatar -->

          <!-- Start:: Provider Name / Shop Name -->
          <base-input
            col="6"
            type="text"
            :placeholder="
              isStoreProvider
                ? $t('PLACEHOLDERS.shop_name')
                : $t('PLACEHOLDERS.provider_name')
            "
            v-model.trim="displayName"
            disabled
          />
          <!-- End:: Provider Name / Shop Name -->

          <!-- Start:: Full Mobile -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.phone')"
            v-model.trim="data.full_mobile"
            disabled
          />
          <!-- End:: Full Mobile -->

          <!-- Start:: City -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.city')"
            v-model.trim="data.city"
            disabled
          />
          <!-- End:: City -->

          <!-- Start:: Email (if exists) -->
          <base-input
            v-if="data.email"
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.email')"
            v-model.trim="data.email"
            disabled
          />
          <!-- End:: Email -->

          <div v-if="data.latitude && data.longitude" class="map_wrapper">
            <GmapMap
              :center="{
                lat: parseFloat(data.latitude),
                lng: parseFloat(data.longitude),
              }"
              :zoom="13"
              map-type-id="terrain"
              style="width: 100%; height: 500px"
            >
              <GmapMarker
                :position="{
                  lat: parseFloat(data.latitude),
                  lng: parseFloat(data.longitude),
                }"
                :clickable="false"
                :draggable="false"
                @click="center = m.position"
              />
            </GmapMap>
          </div>
        </div>
        <!-- End:: Basic Information -->

        <!-- Start:: Store-specific Information -->
        <div v-if="isStoreProvider" class="row mt-4">
          <div class="col-12 mb-3">
            <h5 class="section-title">{{ $t("PLACEHOLDERS.store_info") }}</h5>
          </div>

          <!-- Start:: Commercial Registration (if exists) -->
          <base-input
            v-if="data.commercial_registration_number"
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.commercial_registration_number')"
            v-model.trim="data.commercial_registration_number"
            disabled
          />
          <!-- End:: Commercial Registration -->

          <!-- Start:: Main Categories (if exists) -->
          <base-input
            v-if="data.main_category"
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.main_category')"
            v-model.trim="data.main_category"
            disabled
          />
          <!-- End:: Main Categories -->
        </div>
        <!-- End:: Store-specific Information -->

        <!-- Start:: Cart-specific Information -->
        <div v-if="isCartProvider" class="row mt-4">
          <div class="col-12 mb-3">
            <h5 class="section-title">{{ $t("PLACEHOLDERS.cart_info") }}</h5>
          </div>

          <!-- Start:: Cart Type (if exists) -->
          <base-input
            v-if="data.vehicle"
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.cart_type')"
            v-model.trim="data.cart_type"
            disabled
          />
          <!-- End:: Cart Type -->

          <!-- Start:: Cart Capacity (if exists) -->
          <base-input
            v-if="data.vehicle"
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.cart_capacity')"
            v-model.trim="data.cart_capacity"
            disabled
          />
          <!-- End:: Cart Capacity -->

          <!-- Start:: Cart Description (if exists) -->
          <base-input
            v-if="data.vehicle"
            col="12"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.cart_description')"
            v-model.trim="data.cart_description"
            disabled
          />
          <!-- End:: Cart Description -->

          <!-- Start:: Cart Images (if exists) -->
          <div v-if="data.vehicle && data.cart_images && data.cart_images.length > 0" class="col-12">
            <h6>{{ $t('PLACEHOLDERS.cart_images') }}</h6>
            <div class="images-container">
              <div 
                v-for="(image, index) in data.cart_images" 
                :key="index"
                class="image-wrapper"
              >
                <img 
                  :src="image" 
                  :alt="`Cart Image ${index + 1}`"
                  class="cart-image"
                  @click="openImageModal(image)"
                />
              </div>
            </div>
          </div>
          <!-- End:: Cart Images -->

          <!-- Start:: Cart Video (if exists) -->
          <div v-if="data.vehicle" class="col-6">
            <h6>{{ $t("PLACEHOLDERS.cart_video") }}</h6>
            <video
              controls
              style="width: 100%; max-height: 200px; border-radius: 10px"
            >
              <source :src="data.cart_video" type="video/mp4" />
              {{ $t("PLACEHOLDERS.video_not_supported") }}
            </video>
          </div>
          <!-- End:: Cart Video -->
        </div>
        <!-- End:: Cart-specific Information -->
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
import UploadsPhotos from "../../../components/formInputs/UploadsPhotos.vue";

export default {
  name: "VendorRequestShow",
  components: {
    UploadsPhotos,
  },

  computed: {
    isStoreProvider() {
      return this.data.provider_type_id === 2;
    },

    isCartProvider() {
      return this.data.provider_type_id === 1;
    },

    isServiceProvider() {
      return this.data.provider_type_id === 3;
    },

    displayName() {
      if (this.isStoreProvider && this.data.shop_name) {
        return this.data.shop_name;
      }
      return this.data.name;
    },
  },

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Data Collection To Send
      data: {
        request_number: null,
        name: null,
        shop_name: null,
        full_mobile: null,
        email: null,
        city: null,
        location: null,
        latitude: null,
        longitude: null,
        avatar: null,
        provider_type_id: null,
        provider_type_name: null,
        commercial_registration_number: null,
        main_category: null,

        // Cart-specific data
        vehicle: null,
        cart_type: null,
        cart_capacity: null,
        cart_description: null,
        cart_images: [],
        cart_video: null,

        // Store-specific data
        main_categories: [],
      },
      map: null,
      // End:: Data Collection To Send
    };
  },

  methods: {
    getDay(id) {
      if (id == 1) {
        return this.$t("PLACEHOLDERS.1");
      }
      if (id == 2) {
        return this.$t("PLACEHOLDERS.2");
      }
      if (id == 3) {
        return this.$t("PLACEHOLDERS.3");
      }
      if (id == 4) {
        return this.$t("PLACEHOLDERS.4");
      }
      if (id == 5) {
        return this.$t("PLACEHOLDERS.5");
      }
      if (id == 6) {
        return this.$t("PLACEHOLDERS.6");
      }
      if (id == 7) {
        return this.$t("PLACEHOLDERS.7");
      }
    },
    onFileSelect(files) {
      this.additionalImages = files;
    },
    async onFileRemove(index) {
      this.$delete(this.imgUrls, index);

      const imageId = this.imageId[index];
      try {
        await this.$axios({
          method: "GET",
          url: `delete-file/${imageId}`,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
        this.$delete(this.imgUrls, index);
      } catch (error) {
        this.isWaitingRequest = false;
        // Handle error as needed
        this.$message.error(error.response.data.message);
      }
    },
    // Start:: Get Data To Edit
    async getDataToEdit() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: `vendor-join-requests/${this.$route.params.id}`,
        });

        // Start:: Set Data
        const vendorData = res.data.data.vendor_request;

        // Basic Information (Always Present)
        this.data.request_number = vendorData.request_number;
        this.data.name = vendorData.name;
        this.data.shop_name = vendorData.shop_name;
        this.data.full_mobile = vendorData.full_mobile;
        this.data.email = vendorData.email;
        this.data.city = vendorData.city;
        this.data.location = vendorData.location;
        this.data.latitude = vendorData.latitude;
        this.data.longitude = vendorData.longitude;
        this.data.avatar = vendorData.avatar;
        this.data.provider_type_id = vendorData.provider_type?.id;
        this.data.provider_type_name = vendorData.provider_type?.name;
        this.data.commercial_registration_number =
          vendorData.commercial_registration_number;
        this.data.main_category = vendorData.category_name;
        this.data.vehicle = vendorData.vehicle;
        this.data.cart_type = vendorData?.vehicle?.vehicle_type;
        this.data.cart_capacity = vendorData?.vehicle?.capacity;
        this.data.cart_images = vendorData?.vehicle?.images || [];
        this.data.cart_video = vendorData?.vehicle?.video;
        this.data.cart_description = vendorData?.vehicle?.description;

        // Initialize map if coordinates exist
        if (vendorData.latitude && vendorData.longitude) {
          this.$nextTick(() => {
            this.initializeMap();
          });
        }
        // End:: Set Data
      } catch (error) {
        console.log(error.response.data.message);
      }
    },

    // Start:: Initialize Google Map
    initializeMap() {
      if (window.google && window.google.maps) {
        const mapElement = document.getElementById("map");
        if (mapElement) {
          const lat = parseFloat(this.data.latitude);
          const lng = parseFloat(this.data.longitude);

          this.map = new window.google.maps.Map(mapElement, {
            center: { lat, lng },
            zoom: 15,
          });

          new window.google.maps.Marker({
            position: { lat, lng },
            map: this.map,
            title: this.data.name,
          });
        }
      }
    },

    // Start:: Open Image Modal
    openImageModal(imageUrl) {
      // You can implement a modal here or open image in new tab
      window.open(imageUrl, '_blank');
    },
  },

  async created() {
    // Start:: Fire Methods
    this.getDataToEdit();
    // End:: Fire Methods
  },
};
</script>
<style scoped>
.preview-container img {
  max-width: 100%;
  max-height: 200px;
  border: 1px solid #ccc;
  border-radius: 15px;
}

.section-title {
  color: #2c3e50;
  border-bottom: 2px solid #3498db;
  padding-bottom: 8px;
  margin-bottom: 20px;
  font-weight: bold;
}

.categories-container {
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-top: 5px;
}

.categories-container .v-chip {
  margin: 2px;
}

video {
  border: 1px solid #ddd;
}

.images-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-top: 5px;
}

.image-wrapper {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.image-wrapper:hover {
  transform: scale(1.05);
  cursor: pointer;
}

.cart-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
  transition: opacity 0.3s ease;
}

.cart-image:hover {
  opacity: 0.9;
}

@media (max-width: 768px) {
  .images-container {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
  }
  
  .cart-image {
    height: 120px;
  }
}
</style>
